import React, { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { doc, onSnapshot, getDocFromServer } from 'firebase/firestore';
import { db } from '../firebase/config';
import { checkForUpdate, downloadAndInstall, openInstallSettings } from '../utils/nativeUpdate';

// BUG FIX (Batch 3 follow-up, ask #5): after a deploy, a browser tab or the
// native app's already-loaded WebView kept running the OLD bundle until
// something forced a hard refresh — nothing told anyone a new one existed.
//
// settings/company.currentBuildSha is written by the deploy process itself
// (a plain Admin-SDK write, same git SHA baked into REACT_APP_BUILD_SHA for
// this same build — see the deploy report) — reusing settings/company
// rather than a new collection because it's already publicly readable
// (firestore.rules: `match /settings/company { allow read: if true; }`),
// so this works identically for a signed-in user AND a signed-out one (the
// public storm-report/signing pages) with zero rules changes.
//
// Detection is IDENTICAL on web and native — a plain Firestore SDK read, not
// a browser fetch to an external host, so there's no CORS/same-origin
// wrinkle to work around for the native WebView. What differs is the TAP
// action.
//
// WEB: reloading genuinely fetches the new deployed bundle. Unchanged.
//
// NATIVE: the JS bundle is baked INTO the installed app binary
// (capacitor.config.json's webDir, no live-update/CodePush plugin), so a
// reload would just re-show the SAME stale local assets. Every new build
// therefore needs a new APK — including a pure web change. This used to be a
// dead end that told the owner to go to a store RoofBeam isn't in; now it
// compares the installed versionCode against settings/company.latestAndroid
// and, when a newer APK exists, downloads it and opens Android's package
// installer for a single Install tap. See src/utils/nativeUpdate.js (and its
// Google Play caveat).
const IS_NATIVE = Capacitor.isNativePlatform();
const RUNNING_SHA = process.env.REACT_APP_BUILD_SHA || 'dev';
const POLL_MS = 5 * 60 * 1000; // "every few minutes" per the ask

export default function UpdateBanner() {
  const [newSha, setNewSha] = useState(null);
  // Native update panel: null when closed, else
  // { kind, installed, latest, stage, error, needsPermission, busy }
  const [panel, setPanel] = useState(null);

  useEffect(() => {
    // Never flag a local dev server against itself.
    if (RUNNING_SHA === 'dev') return undefined;

    const checkSha = (sha) => { if (sha && sha !== RUNNING_SHA) setNewSha(sha); };

    // Live listener — reconnects and delivers the latest doc the moment
    // connectivity/foreground resumes on its own; this is the main signal,
    // the explicit poll below is belt-and-suspenders for a long-suspended
    // connection and is what the ask's "poll on focus/every few minutes"
    // literally describes.
    const unsub = onSnapshot(doc(db, 'settings', 'company'), snap => {
      if (snap.exists()) checkSha(snap.data().currentBuildSha);
    }, () => { /* offline/transient — the listener retries on its own */ });

    const pollFresh = async () => {
      try {
        const snap = await getDocFromServer(doc(db, 'settings', 'company'));
        if (snap.exists()) checkSha(snap.data().currentBuildSha);
      } catch { /* offline/transient — next poll or the live listener catches it */ }
    };
    const intervalId = setInterval(pollFresh, POLL_MS);

    let removeNativeResume = () => {};
    if (IS_NATIVE) {
      import('@capacitor/app').then(({ App: CapacitorApp }) => {
        const handle = CapacitorApp.addListener('resume', pollFresh);
        removeNativeResume = () => handle.then(h => h.remove());
      });
    } else {
      document.addEventListener('visibilitychange', onVisible);
      window.addEventListener('focus', pollFresh);
    }
    function onVisible() { if (document.visibilityState === 'visible') pollFresh(); }

    return () => {
      unsub();
      clearInterval(intervalId);
      removeNativeResume();
      if (!IS_NATIVE) {
        document.removeEventListener('visibilitychange', onVisible);
        window.removeEventListener('focus', pollFresh);
      }
    };
  }, []);

  if (!newSha) return null;

  async function handleTap() {
    // Web: a reload is the whole update. Unchanged behaviour.
    if (!IS_NATIVE) { window.location.reload(); return; }

    setPanel({ busy: true, stage: 'Checking…' });
    const res = await checkForUpdate();
    setPanel({ ...res, busy: false });
  }

  async function runInstall() {
    setPanel(p => ({ ...p, busy: true, error: null, needsPermission: false }));
    const res = await downloadAndInstall(stage => setPanel(p => ({ ...p, stage })));
    if (res.ok) {
      // The system installer is now in front. Leave the panel showing so that
      // if the user backs out of it they still know what to do.
      setPanel(p => ({ ...p, busy: false, stage: 'Tap Install in the Android dialog' }));
    } else {
      setPanel(p => ({ ...p, busy: false, stage: null, error: res.error, needsPermission: !!res.needsPermission }));
    }
  }

  const BTN = { fontSize: 13, padding: '9px 14px', borderRadius: 7, border: '1px solid var(--border)', cursor: 'pointer', fontWeight: 600 };

  return (
    <>
      <div
        onClick={handleTap}
        role="button"
        tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleTap(); }}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999,
          background: '#e91e8c', color: '#fff', textAlign: 'center',
          padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
          boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
        }}>
        ⟳ Update available — {IS_NATIVE ? 'tap to update the app' : 'tap to refresh'}
      </div>

      {panel && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => { if (!panel.busy) setPanel(null); }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: 'var(--dark2)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, width: 420, maxWidth: '92vw', color: 'var(--text)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Update RoofBeam</div>

            {panel.installed != null && panel.latest && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
                Installed build {panel.installed} · latest build {panel.latest.versionCode}
                {panel.latest.sha ? ` (${panel.latest.sha})` : ''}
              </div>
            )}

            {panel.busy && <div style={{ fontSize: 13, marginBottom: 14 }}>{panel.stage || 'Working…'}</div>}

            {!panel.busy && panel.stage && !panel.error && (
              <div style={{ fontSize: 13, marginBottom: 14, color: '#2ecc71' }}>{panel.stage}</div>
            )}

            {panel.error && (
              <div style={{ fontSize: 12.5, marginBottom: 14, lineHeight: 1.5, background: 'var(--dark3)', border: '1px solid var(--border)', borderRadius: 6, padding: '10px 12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {panel.error}
              </div>
            )}

            {!panel.busy && panel.kind === 'apk' && !panel.stage && (
              <div style={{ fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>
                A newer RoofBeam app is ready. This downloads it (~10 MB) and
                opens Android's installer — you tap <strong>Install</strong> once.
                Your data and sign-in are kept.
              </div>
            )}

            {!panel.busy && panel.kind === 'none' && (
              <div style={{ fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>
                A new web build has shipped but no newer app build has been
                published yet, so there's nothing to install. Nothing is broken —
                this banner will clear on the next app release.
              </div>
            )}

            {!panel.busy && panel.kind === 'manual' && (
              <div style={{ fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>
                This installed version can't update itself. Install the latest
                RoofBeam app from the link Landon sent (Firebase App
                Distribution), then updates will be one tap from here.
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {panel.kind === 'apk' && !panel.busy && (
                <button onClick={runInstall} style={{ ...BTN, background: '#e91e8c', color: '#fff', borderColor: '#e91e8c' }}>
                  {panel.error ? 'Try again' : 'Download & install'}
                </button>
              )}
              {panel.needsPermission && !panel.busy && (
                <button onClick={openInstallSettings} style={{ ...BTN, background: 'var(--dark3)', color: 'var(--text)' }}>
                  Open settings
                </button>
              )}
              <button onClick={() => setPanel(null)} disabled={panel.busy}
                style={{ ...BTN, background: 'var(--dark3)', color: 'var(--text-muted)', opacity: panel.busy ? 0.5 : 1 }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
